import type {
  AiChatDecisionAnswer,
  AiChatInputRequestData,
} from '@amitable/shared/ai-chat-decisions'
import type { AiChatNavigationContext } from '@amitable/shared/ai-chat-navigation'
import type { AiChatMessage, AiChatSession } from '@/graphql/schema'
import { env } from '@/env'
import { resolveApiUrl } from '@/shared/lib/api-url'

type AiChatToolCall = Omit<AiChatMessage['toolCalls'][number], 'references'> & {
  references?: AiChatMessage['toolCalls'][number]['references']
  /** Set by the API on RECORDS_WRITE tool calls; absent on older events. */
  documentCollectionId?: string | null
}
type StreamMessage = Omit<
  AiChatMessage,
  'inputRequest' | 'files' | 'toolCalls'
> & {
  inputRequest?: AiChatInputRequestData | null
  files?: AiChatMessage['files']
  toolCalls: Array<AiChatToolCall>
}

export type AiChatStreamEvent =
  | { type: 'input_request_updated'; inputRequest: AiChatInputRequestData }
  | { type: 'session_updated'; session: Pick<AiChatSession, 'id' | 'title'> }
  | {
      type: 'user_message'
      userMessage: StreamMessage
    }
  | {
      type: 'tool_call'
      toolCall: AiChatToolCall
    }
  | {
      type: 'assistant_delta'
      delta: string
    }
  | {
      type: 'assistant_done'
      assistantMessage: StreamMessage
    }
  | {
      type: 'error'
      message: string
    }

export type StreamAiChatMessageInput = {
  message: string
  fileIds?: Array<string>
  navigationContext?: AiChatNavigationContext
  sessionId: string
  signal?: AbortSignal
  onEvent: (event: AiChatStreamEvent) => void
}

function buildAiChatStreamUrl(sessionId: string) {
  return resolveApiUrl(
    env.VITE_API_URL,
    `/ai-chat/sessions/${encodeURIComponent(sessionId)}/messages/stream`,
  )
}

async function readErrorMessage(response: Response) {
  const fallback = `AI chat stream failed with status ${response.status}.`

  try {
    const body = await response.text()

    return parseErrorBody(body, fallback)
  } catch {
    return fallback
  }
}

function jsonErrorMessage(value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null || !('message' in value))
    return undefined
  if (typeof value.message === 'string') return value.message
  if (Array.isArray(value.message))
    return value.message
      .filter((entry: unknown): entry is string => typeof entry === 'string')
      .join(', ')

  return undefined
}

function parseErrorBody(body: string, fallback: string) {
  if (!body.trim()) return fallback
  try {
    return jsonErrorMessage(JSON.parse(body) as unknown) || body.trim()
  } catch {
    return body.trim()
  }
}

function parseSseEvent(block: string): AiChatStreamEvent | undefined {
  const data = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n')

  if (!data) {
    return undefined
  }

  return JSON.parse(data) as AiChatStreamEvent
}

function dispatchSseBlocks({
  chunk,
  onEvent,
}: {
  chunk: string
  onEvent: (event: AiChatStreamEvent) => void
}) {
  const blocks = chunk.replaceAll('\r\n', '\n').split('\n\n')
  const pending = blocks.pop() ?? ''

  for (const block of blocks) {
    const event = parseSseEvent(block)

    if (event) {
      onEvent(event)
    }
  }

  return pending
}

export async function streamAiChatMessage({
  navigationContext,
  message,
  fileIds,
  onEvent,
  sessionId,
  signal,
}: StreamAiChatMessageInput) {
  return streamCompletedPost(
    buildAiChatStreamUrl(sessionId),
    { message, navigationContext, ...(fileIds?.length ? { fileIds } : {}) },
    signal,
    onEvent,
  )
}

function inputRequestUrl(sessionId: string, requestId: string, action: string) {
  return resolveApiUrl(
    env.VITE_API_URL,
    `/ai-chat/sessions/${encodeURIComponent(sessionId)}/input-requests/${encodeURIComponent(requestId)}/${action}`,
  )
}

export async function streamAiChatAnswers(input: {
  sessionId: string
  requestId: string
  answers: Array<AiChatDecisionAnswer>
  signal?: AbortSignal
  onEvent: (event: AiChatStreamEvent) => void
}) {
  await streamCompletedPost(
    inputRequestUrl(input.sessionId, input.requestId, 'answers/stream'),
    { answers: input.answers },
    input.signal,
    input.onEvent,
  )
}

async function streamCompletedPost(
  url: string,
  body: unknown,
  signal: AbortSignal | undefined,
  onEvent: (event: AiChatStreamEvent) => void,
) {
  const completed = { current: false }
  await streamPost(url, body, signal, (event) => {
    if (event.type === 'assistant_done') completed.current = true
    if (event.type === 'error') throw new Error(event.message)
    onEvent(event)
  })
  if (!completed.current)
    throw new Error(
      'AI response was interrupted. Accepted messages, answers and completed actions are saved; refresh the conversation before sending a new instruction.',
    )
}

export async function cancelAiChatInputRequest(
  sessionId: string,
  requestId: string,
): Promise<AiChatInputRequestData> {
  const response = await fetch(
    inputRequestUrl(sessionId, requestId, 'cancel'),
    { method: 'POST', credentials: 'include' },
  )
  if (!response.ok) throw new Error(await readErrorMessage(response))

  return response.json() as Promise<AiChatInputRequestData>
}

async function streamPost(
  url: string,
  body: unknown,
  signal: AbortSignal | undefined,
  onEvent: (event: AiChatStreamEvent) => void,
) {
  const response = await fetch(url, {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    method: 'POST',
    signal,
  })

  if (!response.ok) {
    throw new Error(await readErrorMessage(response))
  }

  if (!response.body) {
    throw new Error('AI chat stream response did not include a body.')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let pending = ''
  let done = false

  try {
    while (!done) {
      const result = await reader.read()
      done = result.done

      if (!done) {
        pending = dispatchSseBlocks({
          chunk: `${pending}${decoder.decode(result.value, { stream: true })}`,
          onEvent,
        })
      }
    }

    dispatchSseBlocks({
      chunk: `${pending}${decoder.decode()}\n\n`,
      onEvent,
    })
  } finally {
    reader.releaseLock()
  }
}
